import { ChartSvg, ChartTitle } from "@/components/tool-kit/charts/chart-svg";
import { px } from "@/components/tool-kit/charts/marks";
import { Arrow } from "@/components/tool-kit/diagrams/svg-parts";
import { truncateText } from "@/lib/tool-kit/charts/text";
import { tint } from "@/lib/tool-kit/diagrams/color";
import {
  PRISMA_FONT,
  PRISMA_NOTE_FONT,
  type PrismaBand,
  type PrismaLayout,
} from "@/lib/tool-kit/diagrams/prisma";
import { CHART_COLORS, CHART_INK } from "@/lib/tool-kit/palette";

/*
 * A PRISMA 2020 flow diagram from `layoutPrisma`, drawn like the official template:
 * amber headers over the columns, pale blue phase bars down the left, white boxes with
 * thin black borders, and arrows from each step to the next and to what left the flow.
 * "Black and white" drops the fills for journals that print without colour.
 */

export type PrismaColors = "template" | "mono";

const HEADER_FILL = tint(CHART_COLORS[1], 0.45);
const OTHER_HEADER_FILL = tint(CHART_INK.axis, 0.6);
const PHASE_FILL = tint(CHART_COLORS[4], 0.45);

function Band({ band, fill }: { band: PrismaBand; fill: string }) {
  const { rect } = band;
  const cx = rect.x + rect.width / 2;
  const cy = rect.y + rect.height / 2;
  const room = (band.vertical ? rect.height : rect.width) - 12;
  const text = truncateText(band.label, room, {
    fontSize: PRISMA_FONT,
    bold: true,
  });
  return (
    <g>
      <rect
        fill={fill}
        height={px(rect.height)}
        rx={6}
        stroke={CHART_INK.text}
        strokeWidth={0.75}
        width={px(rect.width)}
        x={px(rect.x)}
        y={px(rect.y)}
      />
      <text
        fill={CHART_INK.text}
        fontSize={PRISMA_FONT}
        fontWeight="bold"
        textAnchor="middle"
        transform={
          band.vertical ? `rotate(-90 ${px(cx)} ${px(cy)})` : undefined
        }
        x={px(cx)}
        y={px(cy + PRISMA_FONT * 0.35)}
      >
        {text}
      </text>
    </g>
  );
}

export function PrismaDiagram({
  id,
  layout,
  colors,
  label,
  description,
}: {
  id: string;
  layout: PrismaLayout;
  colors: PrismaColors;
  label: string;
  description: string;
}) {
  const mono = colors === "mono";
  return (
    <ChartSvg
      description={description}
      height={layout.height}
      id={id}
      label={label}
      width={layout.width}
    >
      <ChartTitle title={layout.title} />
      {layout.headers.map((band, index) => {
        let fill = HEADER_FILL;
        if (mono) {
          fill = CHART_INK.background;
        } else if (index > 0) {
          fill = OTHER_HEADER_FILL;
        }
        return <Band band={band} fill={fill} key={band.label} />;
      })}
      {layout.phases.map((band) => (
        <Band
          band={band}
          fill={mono ? CHART_INK.background : PHASE_FILL}
          key={band.label}
        />
      ))}
      {layout.boxes.map((box) => (
        <g key={box.id}>
          <rect
            fill={CHART_INK.background}
            height={px(box.rect.height)}
            stroke={CHART_INK.text}
            strokeWidth={1}
            width={px(box.rect.width)}
            x={px(box.rect.x)}
            y={px(box.rect.y)}
          />
          {box.lines.map((line) => (
            <text
              fill={CHART_INK.text}
              fontSize={PRISMA_FONT}
              key={`${line.y}-${line.x}`}
              x={px(line.x)}
              y={px(line.y)}
            >
              {line.text}
            </text>
          ))}
        </g>
      ))}
      {layout.arrows.map((points) => (
        <Arrow
          head={7}
          key={points.map(([x, y]) => `${px(x)},${px(y)}`).join(" ")}
          points={points}
          width={1}
        />
      ))}
      {layout.notes.map((line) => (
        <text
          fill={CHART_INK.muted}
          fontSize={PRISMA_NOTE_FONT}
          key={`${line.y}-${line.text}`}
          x={px(line.x)}
          y={px(line.y)}
        >
          {line.text}
        </text>
      ))}
    </ChartSvg>
  );
}
